import { get_attribute_label, get_attribute_name, get_attribute_value } from '/srv/web/wyvr/html/releases/58819/gen/client/shop/core/attributes.js?1733898722603';
import facets from '/srv/web/wyvr/html/releases/58819/gen/client/shop/config/facets.js?1733898722603';
import { get_code_of_attribute } from '/srv/web/wyvr/html/releases/58819/gen/client/shop/facet/facet_helper.js?1733898722603';
import { load_decisions } from '/srv/web/wyvr/html/releases/58819/gen/client/odoscope/api/load_decisions.js?1733898722603';
import { get_odoscope_config } from '/srv/web/wyvr/html/releases/58819/gen/client/odoscope/filter/get_odoscope_config.js?1733898722603';
import { sort_products_by_decision } from '/srv/web/wyvr/html/releases/58819/gen/client/odoscope/api/sort_products_by_decision.js?1733898722603';
import { logger } from '@wyvr/generator/universal.js';
import odoscope_merged_attributes from '/srv/web/wyvr/html/releases/58819/gen/client/odoscope/config/merged_attributes.js?1733898722603';

// store the odoscope config to avoid re-loading data on every filter change
let prev_odoscope_config = null;

const odoscope_enabled = true;

/**
 * Filter the list of given products for the given filter config
 * @param {any[]} products
 * @param {Object} config // filter config { weight: [0.2, 0.4], ... }
 * @returns {any[]} products that match the given filter config
 */
export function filter(products, config) {
    if (!config) {
        return products;
    }

    const keys = Object.keys(config).filter((key) => config[key] !== undefined);
    if (keys.length === 0) {
        return products;
    }

    const attributes = build_attributes_object(config);

    if (!Array.isArray(products)) {
        return [];
    }

    return products.filter((product) => keys.every((key) => matches_filter(product, attributes[key], config[key])));
}

/**
 * Retrieves filtered and sorted products based on Odoscope decisions.
 * @param {Array} products - Array of products to be filtered and sorted.
 * @param {Object} filter_config - Configuration object for filtering products..
 * @param {function} update_fn - The function to update the products, gets called multiple times
 * @returns {Promise<Array>} A promise that resolves to an array of filtered and sorted products.
 */
export async function get_filtered_products(products, filter_config, update_fn) {
    const odoscope_config = get_odoscope_config(filter_config);
    const final_update_fn = update_fn || ((x) => x);

    if (!odoscope_enabled) {
        return final_update_fn(products ? filter(products, filter_config) : [], false);
    }

    // avoid re-loading data on every filter change
    if (!prev_odoscope_config || !equal(prev_odoscope_config, odoscope_config)) {
        prev_odoscope_config = odoscope_config;
    } else {
        return final_update_fn(undefined, false);
    }
    if (!Array.isArray(products)) {
        return final_update_fn([], false);
    }

    const filtering = Object.keys(filter_config ?? {}).length > 0;
    const odoscope_site_id = "kofferworld";
    const odoscope_token = false ? "y35kM" : "RyC0E";
    const odoscope_timeout = Number.parseInt(false ? "10000" : "2000", 10) || 150;

    const [decision_error, odoscope_result] = await load_decisions(odoscope_config, odoscope_site_id, odoscope_token, odoscope_timeout);
    let fallback = false;
    if (decision_error) {
        fallback = true;
        logger.error(decision_error, 'OSC decision error');
    }

    // avoid fallback and error when a filter is present
    if (!filtering && (!Array.isArray(odoscope_result?.variants) || odoscope_result.variants.length === 0)) {
        fallback = true;
        logger.warning('no variants found for odoscope decision for', odoscope_config);
    }
    // fallback to the original products
    if (fallback) {
        logger.warning('fallback to original products from odoscope');
        return final_update_fn(products ? filter(products, filter_config) : [], false);
    }

    // match products by sku
    await sort_products_by_decision(products, odoscope_result, filtering, final_update_fn);
}

/**
 * Builds an attributes object based on the provided configuration.
 * @param {Object} config - The configuration object containing attribute codes and their values.
 * @returns {Object} The attributes object constructed from the config.
 */
function build_attributes_object(config) {
    const attributes = new Map();

    for (const attr of facets.attributes) {
        const code = get_code_of_attribute(attr);
        if (!code) {
            continue;
        }

        const attributeCopy = {
            ...attr,
            attribute: ensure_array(attr.attribute)
        };
        if (attr.type === 'list') {
            attributeCopy.values = config[code] ? combine_attribute_values(config[code]) : [];
        }

        attributes.set(code, attributeCopy);
    }

    return Object.fromEntries(attributes);
}

/**
 * Determine if the values match the filter based on the attribute type.
 * @param {Array} values - The values to check.
 * @param {Object} attribute - The attribute to filter by.
 * @param {Array|boolean} filter_value - The value to filter by.
 * @returns {boolean} - Whether the values match the filter.
 */
function matches_filter(product, attribute, filter_value) {
    if (!attribute) {
        return true;
    }

    // exit early for sliders
    if (attribute.type === 'slider' && filter_value.length < 2) {
        return false;
    }

    const values = get_values_of_attribute(product, attribute);

    if (values.length === 0) {
        return false;
    }

    switch (attribute.type) {
        case 'bool':
            return filter_value === false ? true : values.some((value) => value === (filter_value ? '1' : '0'));
        case 'slider':
            return values.some((value) => value >= filter_value[0] && value <= filter_value[1]);
        case 'list':
            return attribute.values.some((value) => values.includes(value));
        case 'tree':
            return values.some((value) => filter_value[value]);
        default:
            return true;
    }
}

/**
 * Extracts the values of a given attribute from a product and its configurable children.
 * @param {Object} product - The product object.
 * @param {Object} attribute - The attribute object with codes and search_children flag.
 * @returns {Array} - An array of attribute values.
 */
function get_values_of_attribute(product, attribute) {
    const { attribute: attributeCodes, search_children } = attribute;

    // Extract values from the main product
    let values = attributeCodes.flatMap((attributeCode) => extract_values_from_product(product, attributeCode));

    // Optionally extract values from configurable children
    if (search_children && Array.isArray(product.configurable_products)) {
        const childValues = product.configurable_products.flatMap((child) => attributeCodes.flatMap((attributeCode) => extract_values_from_product(child, attributeCode)));
        values = values.concat(childValues);
    }

    return [...new Set(values.flat())];
}

/**
 * Extracts values from a product object based on the provided attribute code.
 * @param {Object} product - The product object from which to extract the attribute value.
 * @param {string} attributeCode - The code of the attribute to extract the value from.
 * @returns {Array} An array of extracted values, which can be numbers or strings.
 */
function extract_values_from_product(product, attributeCode) {
    const value = get_attribute_value(product, attributeCode);
    if (typeof value === 'number') {
        return [value];
    }
    if (typeof value === 'string') {
        return value.split(',').filter(Boolean);
    }
    if (Array.isArray(value)) {
        return value;
    }
    return [];
}

/**
 * Filters and processes products to generate filter options.
 * @param {Array} attributes - The array of attributes.
 * @param {Array} products - The array of products.
 * @returns {Object} The filter options.
 */
export function get_filter_options(attributes, products) {
    const attrs = attributes.filter((item) => item.attribute);
    const child_attrs = attributes.filter((item) => item.search_children);
    const options = {};
    const hash = {};

    for (const product of products) {
        const sku = get_attribute_value(product, 'sku');
        for (const item of attrs) {
            process_attribute(item, sku, product, options, hash);
        }
        if (child_attrs.length > 0) {
            const children = product.configurable_products;
            if (children) {
                for (const childProduct of children) {
                    for (const item of child_attrs) {
                        // sku gets used from the parent
                        process_attribute(item, sku, childProduct, options, hash);
                    }
                }
            }
        }
    }

    // Filter options to only those with products
    const filtered_options = {};
    for (const attr of Object.keys(options)) {
        const filtered = options[attr].filter((item) => {
            item.skus = hash[attr][item.value];
            return item.skus && item.skus.length > 0; // retain only if there are skus/products associated
        });
        if (filtered.length > 0) {
            filtered_options[attr] = filtered;
        }
    }
    return filtered_options;
}

/**
 * Splits attribute names and values into an array of objects.
 * @param {string} name - The attribute names, possibly comma-separated.
 * @param {string} value - The attribute values, possibly comma-separated.
 * @returns {Array} An array of objects with keys and values.
 */
function process_attribute(item, sku, product, options, hash) {
    const attr = item.attribute;
    if (Array.isArray(attr)) {
        if (!item.as) {
            return;
        }
        for (const attr_code of attr) {
            process_attribute_by_code(attr_code, item.as, sku, product, options, hash);
        }
        return;
    }
    process_attribute_by_code(attr, attr, sku, product, options, hash);
}

/**
 * Processes a product attribute by its code and updates the options and hash objects with the attribute details.
 * @param {string} product_attr_code - The product attribute code to process.
 * @param {string} attr_code - The attribute code used for indexing in the options and hash objects.
 * @param {string} sku - The stock keeping unit identifier for the product.
 * @param {Object} product - The product object containing attributes.
 * @param {Object} options - The options object to be updated with attribute details.
 * @param {Object} hash - The hash object to be updated with attribute details and associated SKUs.
 */
function process_attribute_by_code(product_attr_code, attr_code, sku, product, options, hash) {
    if (!product[product_attr_code] || product[product_attr_code] === '0') {
        return;
    }
    const label = get_attribute_label(product, product_attr_code);
    let name = get_attribute_name(product, product_attr_code);
    let value = get_attribute_value(product, product_attr_code);
    if (!options[attr_code]) {
        options[attr_code] = [];
        hash[attr_code] = {};
    }

    if (typeof name === 'object') {
        name = label;
    }
    if (!name || !value) {
        return;
    }
    if (typeof name !== 'string') {
        name = `${name}`;
    }
    if (typeof value !== 'string') {
        value = `${value}`;
    }

    // try split the value and name
    const splitted_name = name.split(',').map((x) => x.trim());
    let splitted_value = value.split(',').map((x) => x.trim());

    // some attributes can contain multiple values, because they get merged with other attributes to produce correct values in odoscope e.g. pim_brand & kw_serie
    if (odoscope_merged_attributes.includes(product_attr_code)) {
        splitted_value = splitted_value.slice(0, 1);
    }

    let entries = [];

    if (splitted_name.length === splitted_value.length) {
        entries = splitted_name
            .map((name, index) => {
                const value = splitted_value[index];
                if (!hash[attr_code][value]) {
                    hash[attr_code][value] = [sku];
                    return { key: name, value: splitted_value[index] };
                }
                hash[attr_code][value].push(sku);
                return undefined;
            })
            .filter((x) => x);
    } else {
        if (!hash[attr_code][value]) {
            entries.push({ key: name, value, skus: [sku] });
            hash[attr_code][value] = [sku];
        } else {
            hash[attr_code][value].push(sku);
        }
    }

    // Only add entries to options if they have products
    entries = entries.filter((entry) => hash[attr_code][entry.value] && hash[attr_code][entry.value].length > 0);
    options[attr_code].push(...entries);
}

/**
 * Ensures the input is an array.
 * @param {*} input - The input to be converted to an array if it is not already one.
 * @returns {Array} The input as an array.
 */
function ensure_array(input) {
    return Array.isArray(input) ? input : [input];
}

/**
 * Combines attribute values from the configuration.
 * @param {Object} attributeConfig - The configuration object for a specific attribute.
 * @returns {Array} The combined list of attribute values.
 */
function combine_attribute_values(attributeConfig) {
    return Object.keys(attributeConfig).flatMap((value) => value.split(','));
}

function equal(a, b) {
    const converter = ([k, v]) => `${k}:${JSON.stringify(v)}`;
    return Object.entries(a).map(converter).sort().join(',') === Object.entries(b).map(converter).sort().join(',');
}
